"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import {
  loginApi,
  logoutApi,
  registerApi,
  getUserApi,
  refreshTokenApi,
  isTokenExpired,
  type User,
  type LoginCredentials,
  type RegisterData,
  type ApiError,
} from "@/lib/api-user";

const ACCESS_KEY = "geesol_access_token";
const REFRESH_KEY = "geesol_refresh_token";

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  getValidToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function saveToStorage() { /* no-op */ }
function clearStorage() { /* no-op */ }

export function AuthProvider({ children }: { children: ReactNode }) {
  // Tokens are in-memory only (not persisted), so the user is always logged out on mount —
  // there is nothing to wait for, so isLoading starts false.
  const [state, setState] = useState<AuthState>({
    user: null,
    accessToken: null,
    isLoading: false,
    isAuthenticated: false,
  });

  const refreshRef = useRef<string | null>(null);
  const accessRef = useRef<string | null>(null);
  // Store current user ID in a ref so callbacks don't go stale
  const userIdRef = useRef<number | null>(null);

  const setTokens = useCallback((access: string, refresh: string) => {
    accessRef.current = access;
    refreshRef.current = refresh;
    saveToStorage();
  }, []);

  const clearAuth = useCallback(() => {
    accessRef.current = null;
    refreshRef.current = null;
    userIdRef.current = null;
    clearStorage();
    setState({ user: null, accessToken: null, isLoading: false, isAuthenticated: false });
  }, []);

  // Track an in-flight refresh so concurrent callers share the same Promise
  const inFlightRefreshPromiseRef = useRef<Promise<string | null> | null>(null);

  const getValidToken = useCallback(async (): Promise<string | null> => {
    const access = accessRef.current;
    const refresh = refreshRef.current;

    if (access && !isTokenExpired(access)) return access;
    if (!refresh) return null;

    if (inFlightRefreshPromiseRef.current) return inFlightRefreshPromiseRef.current;

    inFlightRefreshPromiseRef.current = (async () => {
      try {
        const { access: newAccess, refresh: newRefresh } = await refreshTokenApi(refresh);
        setTokens(newAccess, newRefresh ?? refresh);
        setState((prev) => ({ ...prev, accessToken: newAccess }));
        return newAccess;
      } catch {
        clearAuth();
        return null;
      } finally {
        inFlightRefreshPromiseRef.current = null;
      }
    })();

    return inFlightRefreshPromiseRef.current;
  }, [setTokens, clearAuth]);


  const login = useCallback(
    async (credentials: LoginCredentials) => {
      const tokens = await loginApi(credentials);
      setTokens(tokens.token, tokens["refresh-token"]);
      userIdRef.current = tokens.user.id;
      setState({
        user: tokens.user,
        accessToken: tokens.token,
        isLoading: false,
        isAuthenticated: true,
      });
    },
    [setTokens],
  );

  const register = useCallback(async (data: RegisterData) => {
    await registerApi(data);
    // Registration succeeds — caller should then call login
  }, []);

  const logout = useCallback(async () => {
    const token = accessRef.current;
    if (token) {
      try { await logoutApi(token); } catch { /* ignore */ }
    }
    clearAuth();
  }, [clearAuth]);

  const refreshUser = useCallback(async () => {
    const token = await getValidToken();
    if (!token) return;
    const userId = userIdRef.current;
    if (!userId) return;
    try {
      const user = await getUserApi(userId, token);
      setState((prev) => ({ ...prev, user, accessToken: token }));
    } catch {
      clearAuth();
    }
  }, [getValidToken, clearAuth]);

  return (
    <AuthContext.Provider
      value={{ ...state, login, register, logout, refreshUser, getValidToken }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

// Convenience re-export for use in API error handling
export type { ApiError };

// Suppress unused import warnings for keys kept for documentation purposes
void ACCESS_KEY;
void REFRESH_KEY;
